// BILLING-PLANS (§6.1): the next invoice as Stripe last previewed it — its date, the estimated
// total and the lines: the plan and extra people, maintenance per tier, retainers, pending
// prorations named by DE, and the discount line. Read from the mirrored snapshot, never live.
import {
  InfoTip,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
} from "@companyos/ui";
import { formatDate, formatMoney } from "../../format";
import type { UpcomingInvoice } from "../plan-queries";
import { SectionHead } from "./parts";

export function NextInvoice({ upcoming }: { upcoming: UpcomingInvoice }) {
  const cur = upcoming.currency;
  return (
    <section
      aria-labelledby="billing-next"
      className="flex flex-col gap-2"
      data-slot="next-invoice"
    >
      <SectionHead id="billing-next" title="Next invoice">
        <span className="text-[13px] text-muted-foreground">
          {upcoming.next_payment_at ? formatDate(upcoming.next_payment_at) : "Date to come"}
        </span>
      </SectionHead>
      <Table className="text-[13px] [&_td]:py-1.5 [&_th[scope=row]]:py-1.5">
        <TableCaption className="sr-only">The next invoice's lines.</TableCaption>
        <TableBody>
          {upcoming.lines.map((l) => (
            <TableRow key={l.id} data-line={l.kind}>
              <TableHead scope="row" className="font-normal">
                <span className="inline-flex items-center gap-1">
                  {l.description}
                  {l.proration ? (
                    <InfoTip label={`About ${l.description}`}>
                      A change mid-month, charged or credited for the days it applies.
                    </InfoTip>
                  ) : null}
                </span>
              </TableHead>
              <TableCell
                className={`text-right tabular-nums ${l.amount_minor < 0 ? "text-success-text" : ""}`}
              >
                {l.amount_minor > 0 && l.proration ? "+" : ""}
                {formatMoney(l.amount_minor, cur)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableHead scope="row" className="font-medium">
              <span className="inline-flex items-center gap-1">
                Estimated total
                <InfoTip label="About the estimate">
                  Stripe's preview; people or DEs added before the date change it. Tax, if any, is
                  added on the invoice.
                </InfoTip>
              </span>
            </TableHead>
            <TableCell className="text-right font-medium tabular-nums" data-field="next-total">
              {formatMoney(upcoming.total_minor, cur)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </section>
  );
}
